/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2018
 */

/*global angular*/
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NexleaderEcourseRaliVideoComponent } from '../../../../ecourse/components/ecourse-rali-video/ecourse-rali-video.component';
import { NexleaderPreIpsatChecklistComponent } from '../../onboarding/onboarding-preassessments/pre-ipsat-checklist/pre-ipsat-checklist.component';
import { NexleaderOnboardingPersonalValuesInventoryComponent } from '../../onboarding/onboarding-preassessments/onboarding-personal-values-inventory/onboarding-personal-values-inventory.component';
import { EnumsService } from '../../../../../services/enum.service';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { UserService } from '../../../../../services/user.service';
import { SuccessService } from '../../../../../services/success.service';
import { tap, catchError, of } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-nexleader-ecourse-module-five',
  standalone: true,
  imports: [CommonModule, NexleaderEcourseRaliVideoComponent, NexleaderPreIpsatChecklistComponent,
    NexleaderOnboardingPersonalValuesInventoryComponent],
  templateUrl: './ecourse-module-five.component.html'
})
export class NexleaderEcourseModuleFiveComponent implements OnInit {
  /**
   * user
   *
   * A user object we can reference to and update as the onboarding user goes throughout the process.
   */
  @Input() user: any;

  /**
   * handleNextStep
   *
   * Allows data that is edited on this ecourse module to be saved.
   */
  @Output() handleNextStep = new EventEmitter<void>();


  /**
   * moduleSteps
   * 
   * Controls the steps the user can move between for the ecourse module.
   */
  moduleSteps: string[] = ['engagement', 'assessment'];

  /**
   * currentStep
   * 
   * Determines the current step the user is on in the ecourse module.
   */
  currentStep: string = this.moduleSteps[0];
  currentPrerequisite: any;

  constructor(
    private userService: UserService,
    private enumsService: EnumsService,
    private errorHandler: ErrorHandlerService,
    private successHandler: SuccessService
  ) { }

  ngOnInit(): void {
    this.load();

    // Automatically move the user to the engagement step if they've already completed the PVI
    if (this.user && this.user.prerequisites && this.user.prerequisites.completedSpiritualGifts) {
      this.currentStep = this.moduleSteps[this.moduleSteps.indexOf(this.currentStep) + 1];
    }
  }

  /**
   * load()
   * 
   * Loads all required enums for the module.
   */
  load(): void {
    this.enumsService.getEnums().pipe(
      tap((enums) => {
        this.currentPrerequisite = enums.Prerequisites.SPIRITUAL_GIFTS;
      }), catchError((error) => {
        this.errorHandler.handleError(error);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * nextStep()
   * 
   * Handles moving the user to the next step (WITHIN THE MODULE).
   *  Calls the handleNextStep() function if the user is on the last step.
   *  ie: moving from learning_assessment to engagement
   */
  nextStep(): void {
    const currentStepIndex = this.moduleSteps.indexOf(this.currentStep);
    if (currentStepIndex < this.moduleSteps.length - 1) {
      this.currentStep = this.moduleSteps[currentStepIndex + 1];
      this.save();
    } else {
      this.save();
      this.handleNextStep.emit();
    }
  }

  /**
   * mapContinueButtonTextForStep()
   * 
   * Maps text for the continue buttons given the current step of the module the user
   *  is on.
   */
  mapContinueButtonTextForStep(): string | null {
    switch (this.currentStep) {
      case 'engagement':
        return 'Continue to Personal Values Inventory';
      default:
        return null;
    }
  }

  /**
   * save()
   * 
   * Saves a user. To be called when an MBTI option is selected.
   */
  save(): void {
    this.user.prerequisites.completedSpiritualGifts = true;
    this.userService
      .saveUser(this.user)
      .pipe(
        tap((_) => {
          this.successHandler.handle();
        }),
        catchError((error) => {
          this.errorHandler.handleError(error);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * canProceedToNextStep()
   * 
   * Determines if the user is able to continue to the next module of the ecourse.
   */
  canProceedToNextStep(): boolean {
    return this.user &&
      this.user.prerequisites &&
      this.user.prerequisites.spiritualGifts &&
      this.user.prerequisites.spiritualGifts.length === 3 &&
      this.user.prerequisites.spiritualGifts.every(this.nonemptyStringObj);
  }

  /**
   * nonemptyStringObj()
   * 
   * helper function
   * 
   * Determines if the object contains a key of name that has a string value.
   * 
   * @param {Object} strO - An object to check.
   */
  nonemptyStringObj(strO: any): boolean {
    if (!strO) return false;
    return typeof strO.name === 'string' && strO.name.trim() !== '';
  }
}
